import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Disclose,
  Headline,
  Loading,
  Proportion,
  Refusal,
  Term,
} from "@lopie/kernel-ui";

/**
 * What may they do that they never have?
 *
 * ## The question no other screen here asks
 *
 * Apps answers *what is this app permitted*. Activity answers *what
 * happened*. Nothing puts the two beside each other — and the gap between
 * them is the most useful fact this kernel holds about itself, because every
 * permission on the wrong side of it is one somebody can take away today at
 * no cost.
 *
 * That reconciliation normally takes an outside reviewer a week of reading
 * code and guessing. It takes this kernel a page load, and the reason is
 * structural rather than clever: **the same system that sets the ceiling
 * writes the record.** A platform where the app declares its own permissions,
 * or where the log is written by the thing being logged, cannot produce this
 * screen at all — it can produce a screen that looks like it.
 *
 * ## Why the answer is exact, which is the part that matters
 *
 * The audit log is **partitioned by action**. `GET /audit?action=person.update`
 * is one query into one partition, not a scan of a log filtered afterwards.
 * So asking *has this verb ever happened* costs one read that either returns
 * a row immediately or exhausts an empty partition immediately, and the
 * answer is about the whole history rather than about a window.
 *
 * That is why this screen asks for `limit=1` per verb and no attribute
 * filter. Adding `app_type` would make each query an attribute filter that
 * runs *after* the read — which walks the whole partition, can stop early,
 * and turns an exact answer into a sample. The per-app version of this
 * question is a different and more expensive screen, and pretending this one
 * answers it would be the exact failure the screen exists to expose.
 *
 * So the sentence here is verb-level and says so everywhere: *nobody has ever
 * done this*, never *this app has never done this*.
 *
 * ## Four states, because fewer would each be a different lie
 *
 * - **Used** — a row came back. Exact, and it carries when.
 * - **Never used** — no row, and the partition was exhausted. Exact.
 * - **Not recorded** — the verb is outside the log's vocabulary entirely.
 *   There is no partition to query, so using it leaves nothing behind.
 * - **Cannot tell** — no row, and the read stopped early.
 *
 * **The third one is the finding, and the first version of this screen hid
 * it.** It asked about all twenty-three declared verbs, twelve came back 400,
 * and it filed every one of them under "cannot tell" — which was true of none
 * of them. The kernel had answered precisely and the screen had rounded the
 * answer off:
 *
 *     'agent.run' is not an action this kernel writes. A verb outside the
 *     vocabulary has no rows, and answering an empty page would read as
 *     'it never happened'.
 *
 * *Nobody has done this* and *doing this leaves no trace* are opposite facts
 * about a permission, and a screen built to expose the gap between what is
 * allowed and what is recorded cannot be the thing that closes it by
 * accident. So the vocabulary is now fetched **first**, and a verb outside it
 * is reported rather than asked about — which also turns twelve failed
 * requests into zero.
 *
 * What it does not do is say which of those are fine. Some are reads —
 * `audit.read` and `access.read` change nothing, and a write log having no
 * row for them is correct. Others change real state. Sorting one from the
 * other is a judgement about each verb and this screen does not have the data
 * to make it, so it reports the fact and names both possibilities. The
 * kernel's own `audit.py` already calls one of them out by name as "a gap in
 * LO-73", which is the sort of thing that is easier to leave in a docstring
 * than on a screen.
 *
 * ## What this does not prove
 *
 * A permission nobody has used is not a permission nobody needs. The quarterly
 * job has not run yet; the incident that calls for it has not happened. The
 * claim is narrow and exact — *no row in this log names this verb* — and the
 * decision it supports is a conversation, not a deletion. That sentence is on
 * the screen rather than in this comment, because the reader is the one who
 * needs it.
 */

/**
 * How many verbs to ask about.
 *
 * Each is one partition read, so this is a ceiling on requests rather than on
 * meaning. It is stated on screen when it bites: a figure computed from part
 * of the list, presented as the whole, is worse than no figure.
 */
const MAX_VERBS = 60;

/** Used / never / unknown, as the palette already names them. */
const TONE = {
  used: "kernel.permitted",
  never: "kernel.attention",
  // Violet is the palette's "nobody was watching" colour and it is the right
  // one here for the same reason: this is not a worse or better state than
  // never-used, it is a different axis — whether the log would know.
  unrecorded: "kernel.unattended",
  unknown: "text.disabled",
};

const WORD = {
  used: "used",
  never: "never",
  unrecorded: "leaves no record",
  unknown: "cannot tell",
};

function when(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months === 1 ? "a month ago" : `${months} months ago`;
}

/**
 * One verb, its state, and who asked for it.
 *
 * A row rather than a cell in a grid. A grid of apps × verbs is the image
 * this screen wants to be and it would be a false one: usage is measured per
 * verb and not per app, so a filled cell under an app's column would be
 * claiming something no query here asked.
 */
function Verb({ row }) {
  const ago = when(row.at);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 18rem) 1fr auto" },
        gap: { xs: 0.5, sm: 2 },
        alignItems: "baseline",
        py: 1.25,
        borderTop: 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", minWidth: 0 }}>
        {/* The state reads as a word before it reads as a colour. A dot on
            its own would be the semantic-colour-as-only-signal failure the
            accessibility rule names. */}
        <Box
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flex: "none",
            backgroundColor: row.state === "used" ? TONE.used : "transparent",
            border: row.state === "used" ? 0 : 1,
            borderColor: TONE[row.state],
          }}
        />
        <Typography variant="mono" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>
          {row.verb}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: "wrap", rowGap: 0.5, minWidth: 0 }}
      >
        {row.apps.map((app) => (
          <Chip
            key={app.app_type}
            size="small"
            variant="outlined"
            label={app.label || app.app_type}
          />
        ))}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        {row.state === "used" ? ago || "at some point" : WORD[row.state]}
      </Typography>
    </Box>
  );
}

function Group({ title, note, rows }) {
  if (!rows.length) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{ display: "block", color: "text.primary" }}
      >
        {title} — {rows.length}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, maxWidth: "62ch" }}
      >
        {note}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        {rows.map((row) => (
          <Verb key={row.verb} row={row} />
        ))}
      </Box>
    </Box>
  );
}

export default function Unused({ get, onEffective }) {
  const [apps, setApps] = useState(null);
  const [rows, setRows] = useState(null);
  const [truncated, setTruncated] = useState(0);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    setFailure(null);
    try {
      // The log's own vocabulary, first and in one request.
      //
      // Asking about a verb the log has no partition for is a 400 — a correct
      // and precisely worded one — and the first version of this screen
      // discovered that twelve times per load by failing. Reading the
      // vocabulary up front turns twelve refusals into one fact known before
      // anything is asked, and it is the fact the screen most needs: a verb
      // that is not in here leaves no row when it is used.
      const [body, feed] = await Promise.all([
        get("/apps"),
        get("/audit", { limit: 1 }),
      ]);
      const recorded = new Set(feed.vocabulary?.actions || []);
      const installed = body.apps || [];
      setApps(installed);

      // Every verb at least one app declares, and who declares it. Both
      // halves of the declaration count: a verb permitted only when nobody is
      // signed in is still a permitted verb, and it is the more interesting
      // half — it is the one that can happen at three in the morning.
      const byVerb = new Map();
      for (const app of installed) {
        const declared = new Set([
          ...(app.actions || []),
          ...(app.service_actions || []),
        ]);
        for (const verb of declared) {
          if (!byVerb.has(verb)) byVerb.set(verb, []);
          byVerb.get(verb).push(app);
        }
      }

      const verbs = [...byVerb.keys()].sort();
      setTruncated(Math.max(verbs.length - MAX_VERBS, 0));

      // One partition read each. `sort=desc` so a used verb also answers
      // *when was the last time*, which costs nothing on top of the same row.
      const answers = await Promise.all(
        verbs.slice(0, MAX_VERBS).map(async (verb) => {
          const base = { verb, apps: byVerb.get(verb) };
          // Outside the log's vocabulary. Not "we looked and found nothing" —
          // there is nowhere to look, because nothing writes a row for this.
          if (recorded.size && !recorded.has(verb)) {
            return { ...base, state: "unrecorded" };
          }
          try {
            const page = await get("/audit", {
              action: verb,
              limit: 1,
              sort: "desc",
            });
            const hit = (page.items || [])[0];
            if (hit) return { ...base, state: "used", at: hit.at };
            // No row. Whether that means *never* or *we stopped looking* is
            // the whole integrity of this screen, and the kernel is the one
            // that knows.
            return {
              ...base,
              state: page.stopped_early ? "unknown" : "never",
              detail: page.stopped_detail,
            };
          } catch (e) {
            return { ...base, state: "unknown", detail: e?.body?.detail };
          }
        }),
      );
      setRows(answers);
    } catch (e) {
      setFailure(e);
      setRows(null);
    }
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  const split = useMemo(() => {
    const all = rows || [];
    return {
      used: all.filter((r) => r.state === "used"),
      never: all.filter((r) => r.state === "never"),
      unrecorded: all.filter((r) => r.state === "unrecorded"),
      unknown: all.filter((r) => r.state === "unknown"),
    };
  }, [rows]);

  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }
  if (!rows) {
    return <Loading label="Asking the log about every permission in turn" />;
  }

  const total = rows.length;
  const never = split.never.length;

  return (
    <Stack spacing={4} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow="Permitted and never once used"
        figure={total ? `${never} of ${total}` : "—"}
        tone={never ? "attention" : undefined}
        caption={
          never === 1
            ? "permission has never been exercised"
            : "permissions have never been exercised"
        }
        basis={
          "One read per verb, straight into that verb's own partition of the "
          + "log — so this is the whole history of this kernel and not a "
          + "recent window. It counts only the verbs the log actually "
          + "records; the ones it does not are called out separately rather "
          + "than folded in as unused."
        }
        lede={
          <>
            Every app here declares what it may do, and the kernel records
            everything that was done. This is the two lists laid against each
            other: the{" "}
            <Term name="verbs">verbs</Term> somebody was granted and nobody
            has ever used.
          </>
        }
      />

      {total ? (
        <Box>
          <Proportion
            segments={[
              {
                value: split.used.length,
                colour: TONE.used,
                label: "used",
                detail:
                  "Have happened at least once. A row in the log names each.",
              },
              {
                value: never,
                colour: TONE.never,
                label: "never used",
                detail:
                  "No row anywhere in the log names them, and the read finished.",
              },
              {
                value: split.unrecorded.length,
                colour: TONE.unrecorded,
                label: "leaves no record",
                detail:
                  "Outside the log's vocabulary — there is no partition to look in, because using these writes no row.",
              },
              {
                value: split.unknown.length,
                colour: TONE.unknown,
                label: "cannot tell",
                detail:
                  "The read stopped early, so this is not a claim that they never happened.",
              },
            ]}
          />
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}
          >
            <Legend tone={TONE.used} label={`${split.used.length} used`} />
            <Legend tone={TONE.never} label={`${never} never used`} hollow />
            {split.unrecorded.length ? (
              <Legend
                tone={TONE.unrecorded}
                label={`${split.unrecorded.length} leave no record`}
                hollow
              />
            ) : null}
            {split.unknown.length ? (
              <Legend
                tone={TONE.unknown}
                label={`${split.unknown.length} cannot tell`}
                hollow
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {/* Directly under the figure, because it changes what the figure
          means. A count of "never used" is only as good as the log's reach,
          and this is the part of the ceiling the log cannot see at all. */}
      {split.unrecorded.length ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ borderColor: "kernel.unattended" }}
        >
          <AlertTitle>
            {split.unrecorded.length} of these leave no record when they are
            used
          </AlertTitle>
          The log is written per verb, and{" "}
          {split.unrecorded.length === 1 ? "this one is" : "these are"} outside
          its vocabulary — so the kernel refuses to answer{" "}
          <Box component="em">has this ever happened</Box> rather than
          returning an empty page that would read as &ldquo;no&rdquo;. Some of them are reads, and a log of writes having
          nothing for a read is correct. The rest change something and leave
          nothing behind, which is a gap rather than a design. This screen
          reports which verbs; deciding which kind each one is belongs to
          whoever owns it.
        </Alert>
      ) : null}

      {/* The one qualification that decides whether this screen is useful or
          dangerous, and it is not tucked into a disclosure. */}
      <Alert severity="info" variant="outlined">
        <AlertTitle>What this does not prove</AlertTitle>
        A permission nobody has used is not a permission nobody needs. The
        quarterly job may not have run yet; the incident that calls for it may
        not have happened. The exact claim is narrower than it looks — no row
        in this log names this verb — and it is the start of a conversation
        with whoever owns the app, not a list to delete.
      </Alert>

      {truncated ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>
            {truncated} more {truncated === 1 ? "verb is" : "verbs are"} declared
            and not asked about
          </AlertTitle>
          This screen asks about {MAX_VERBS} at a time, one request each. The
          figure above is of the {total} it asked, not of everything declared —
          said here rather than quietly presented as the whole.
        </Alert>
      ) : null}

      <Group
        title="Never used"
        note="No row anywhere in the log names these. Each one is permitted today and is doing nothing but widening what a mistake or a stolen token could reach."
        rows={split.never}
      />

      {split.unrecorded.length ? (
        <Group
          title="Leaves no record"
          note="The log has no partition for these, so using one writes nothing anybody can read back. A read with nothing to record is fine; a change with nothing to record is not, and the two are not separable from here."
          rows={split.unrecorded}
        />
      ) : null}

      {split.unknown.length ? (
        <Group
          title="Cannot tell"
          note="The read did not finish, so the honest answer is that we do not know rather than that it never happened."
          rows={split.unknown}
        />
      ) : null}

      <Disclose label={`The ${split.used.length} that are in use`}>
        <Group
          title="Used"
          note="Each has happened at least once, and the date is the most recent time. Nothing here is a finding; it is the other side of the figure, and a figure whose other side you cannot see is one you cannot check."
          rows={split.used}
        />
      </Disclose>

      {apps ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          Read from {apps.length} installed{" "}
          {apps.length === 1 ? "app" : "apps"}. Usage is counted per verb and
          never per app: a verb two apps declare and one has used reads as used
          here, because asking which of the two would be a different and much
          more expensive question than this screen asks.
        </Typography>
      ) : null}
    </Stack>
  );
}

/** A dot and a word, because the dot alone is not a signal for everybody. */
function Legend({ tone, label, hollow }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
      <Box
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: hollow ? "transparent" : tone,
          border: hollow ? 1 : 0,
          borderColor: tone,
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
